/**
 * totp-login-step.tsx — Bước xác thực 2 lớp (TOTP) giữa đăng nhập, sau khi mật khẩu đã đúng.
 *
 * Hai trường hợp (xem backend auth-service.ts#LoginOutcome):
 *  - `verify`: tài khoản ĐÃ bật TOTP → nhập mã 6 số (app Authenticator) hoặc 1 mã dự phòng.
 *  - `setup`: vai trò BẮT BUỘC (owner / platform-admin) nhưng CHƯA bật → quét QR, xác nhận mã đầu
 *    → bật ngay + hiện 10 mã dự phòng MỘT LẦN DUY NHẤT trước khi vào được hệ thống.
 *
 * Dùng chung cho đăng nhập công ty (login-form.tsx) và Platform (platform-login-page.tsx) — nhận vào
 * các hàm gọi API tương ứng (mỗi bên có store/endpoint riêng) nên không phụ thuộc store cụ thể nào.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Check, Copy, Loader2, ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { TotpChallenge } from '@/types/api'

export interface TotpLoginStepProps<TUser> {
  challenge: TotpChallenge
  /** Tài khoản ĐÃ bật TOTP — xác minh mã 6 số hoặc mã dự phòng. */
  onVerify: (ticket: string, code: string) => Promise<TUser>
  /** Bắt buộc, CHƯA bật — bước 1: sinh secret + QR. */
  onSetupStart: (ticket: string) => Promise<{ otpauthUri: string; qrDataUrl: string }>
  /** Bắt buộc, CHƯA bật — bước 2: xác nhận mã đầu, trả về user + 10 mã dự phòng (hiện 1 lần). */
  onSetupConfirm: (ticket: string, code: string) => Promise<{ user: TUser; backupCodes: string[] }>
  /** Đã có token (kể cả sau khi đã lưu mã dự phòng) — vào hệ thống. */
  onDone: (user: TUser) => void
  /** Quay lại form đăng nhập (ticket hết hạn, nhập sai nhiều lần muốn đổi tài khoản…). */
  onBack: () => void
  /** Rút thông điệp lỗi tiếng Việt từ lỗi API — mỗi bên (công ty/Platform) có hàm riêng. */
  errorMessage: (err: unknown) => string
}

type Phase = 'verify' | 'setup-loading' | 'setup-qr' | 'backup-codes'

export function TotpLoginStep<TUser>({
  challenge, onVerify, onSetupStart, onSetupConfirm, onDone, onBack, errorMessage,
}: TotpLoginStepProps<TUser>) {
  const { t } = useTranslation('authPages')
  const [phase, setPhase] = useState<Phase>(challenge.mode === 'setup' ? 'setup-loading' : 'verify')
  const [qr, setQr] = useState<{ otpauthUri: string; qrDataUrl: string } | null>(null)
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [backupCodes, setBackupCodes] = useState<string[]>([])
  const [pendingUser, setPendingUser] = useState<TUser | null>(null)
  const [copied, setCopied] = useState(false)

  // Bắt buộc thiết lập: tự gọi bước 1 (sinh secret + QR) ngay khi vào màn này.
  // StrictMode (dev) mount→cleanup→mount lại effect này — `startedRef` giữ ĐÚNG 1 promise request dùng chung
  // cho mọi lần effect chạy (không gọi lại API lần 2, vì backend ghi đè secret mỗi lần gọi — totp-service.ts —
  // nên 2 request chạy đua dễ khiến mã QR hiện lên không khớp secret backend thực sự đang chờ). Mỗi lần effect
  // chạy vẫn tự gắn `.then` riêng (biến `alive` cục bộ của chính lần đó) để nhận được kết quả khi promise xong —
  // KHÔNG được return sớm bỏ qua việc gắn `.then`, nếu không lần mount sau cùng (lần "thật" còn sống) sẽ không
  // bao giờ nhận được QR, màn hình kẹt ở vòng xoay mãi mãi (đã xảy ra thật khi test local có StrictMode).
  const startedRef = useRef<Promise<{ otpauthUri: string; qrDataUrl: string }> | null>(null)
  useEffect(() => {
    if (phase !== 'setup-loading') return
    let alive = true
    if (!startedRef.current) startedRef.current = onSetupStart(challenge.ticket)
    startedRef.current
      .then((d) => { if (alive) { setQr(d); setPhase('setup-qr') } })
      .catch((err) => { if (alive) { setError(errorMessage(err)); setPhase('setup-qr') } })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase])

  async function submitVerify(e: FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      onDone(await onVerify(challenge.ticket, code.trim()))
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  async function submitSetupConfirm(e: FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      const { user, backupCodes: codes } = await onSetupConfirm(challenge.ticket, code.trim())
      setPendingUser(user)
      setBackupCodes(codes)
      setPhase('backup-codes')
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  function copyAll() {
    void navigator.clipboard?.writeText(backupCodes.join('\n')).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const backLink = (
    <button type="button" onClick={onBack} className="block w-full text-center text-sm text-muted-foreground hover:underline">
      {t('totp.back')}
    </button>
  )

  if (phase === 'backup-codes') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-emerald-600">
          <ShieldCheck className="h-5 w-5" />
          <p className="text-sm font-medium">{t('totp.enabled')}</p>
        </div>
        <p className="text-sm text-muted-foreground">
          <Trans t={t} i18nKey="totp.backupCodesHint" components={{ strong: <strong /> }} />
        </p>
        <div className="grid grid-cols-2 gap-2 rounded-lg border bg-muted p-4 font-mono text-sm">
          {backupCodes.map((c) => <div key={c}>{c}</div>)}
        </div>
        <Button type="button" variant="outline" className="h-11 w-full rounded-lg" onClick={copyAll}>
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {copied ? t('totp.copied') : t('totp.copyAll')}
        </Button>
        <Button type="button" className="h-12 w-full rounded-lg text-base" onClick={() => pendingUser && onDone(pendingUser)}>
          {t('totp.savedContinue')}
        </Button>
      </div>
    )
  }

  if (phase === 'setup-loading') {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (phase === 'setup-qr') {
    const secret = qr?.otpauthUri.match(/secret=([^&]+)/)?.[1]
    return (
      <form onSubmit={submitSetupConfirm} className="space-y-4">
        <p className="text-sm text-muted-foreground">
          <Trans t={t} i18nKey="totp.setupRequiredHint" components={{ strong: <strong className="text-foreground" /> }} />
        </p>
        {qr && (
          <div className="flex flex-col items-center gap-2">
            <img src={qr.qrDataUrl} alt={t('totp.qrAlt')} className="h-44 w-44 rounded-lg border p-2" />
            {secret && (
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer">{t('totp.manualEntry')}</summary>
                <code className="mt-1 block break-all">{secret}</code>
              </details>
            )}
          </div>
        )}
        <div>
          <Label htmlFor="totp-setup-code" className="sr-only">{t('totp.codeLabel')}</Label>
          <Input
            id="totp-setup-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder={t('totp.codePlaceholder')}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={6}
            autoFocus
            required
          />
        </div>
        {error && <p className="rounded-lg bg-destructive/10 p-2.5 text-sm text-destructive" role="alert">{error}</p>}
        <Button type="submit" className="h-12 w-full rounded-lg text-base" disabled={loading || code.trim().length !== 6}>
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {t('totp.confirmEnable')}
        </Button>
        {backLink}
      </form>
    )
  }

  // phase === 'verify'
  return (
    <form onSubmit={submitVerify} className="space-y-4">
      <p className="text-sm text-muted-foreground">{t('totp.verifyIntro')}</p>
      <div>
        <Label htmlFor="totp-code" className="sr-only">{t('totp.codeLabel')}</Label>
        <Input
          id="totp-code"
          autoComplete="one-time-code"
          placeholder={t('totp.codeOrBackupPlaceholder')}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          autoFocus
          required
        />
      </div>
      {error && <p className="rounded-lg bg-destructive/10 p-2.5 text-sm text-destructive" role="alert">{error}</p>}
      <Button type="submit" className="h-12 w-full rounded-lg text-base" disabled={loading || !code.trim()}>
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {t('totp.confirm')}
      </Button>
      {backLink}
    </form>
  )
}
